import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users as UsersIcon, Plus, Edit, Trash2, Search, X, KeyRound, Check, Copy,
  MessageCircle, Upload,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useLang } from "../../context/LangContext";
import { BulkImportCustomersDialog } from "../../pages/admin/CustomersManagement";
import { VatLookupButton } from "../../components/VatLookupButton";
import { API } from "./_shared";
import { PlafondCell, PlafondRequestDialog } from "../../components/plafond";
import { DeliveryBadges, useCapsSummary } from "../../components/logistics/DeliveryInfo";

/**
 * CustomerForm — create/update a customer (used both from CustomersList and the
 * inline "new customer" flow inside the new-order page). Exported because it is
 * referenced by other modules.
 */
export const CustomerForm = ({ customer, onSave, onClose }) => {
  const { t } = useLang();
  const [form, setForm] = useState(() => customer ? {
    ...customer,
    commission_override: customer.commission_override != null ? Math.round(customer.commission_override * 10000) / 100 : "",
  } : {
    business_name: "", contact_name: "", vat_number: "", fiscal_code: "", sdi_code: "",
    pec: "", email: "", phone: "", iban: "", address: "", city: "", cap: "", province: "",
    delivery_addresses: [], notes: "", is_foreign: false, pricelist_type_id: "", commission_override: "",
  });
  const [pricelistTypes, setPricelistTypes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/pricelist-types`).then((r) => setPricelistTypes(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.business_name || !form.vat_number || !form.address || !form.city) {
      toast.error("Compila i campi obbligatori (*)");
      return;
    }
    try {
      const payload = {
        ...form,
        pricelist_type_id: form.pricelist_type_id || null,
        commission_override: form.commission_override === "" || form.commission_override == null ? null : parseFloat(form.commission_override) / 100,
      };
      if (customer?.id) await axios.put(`${API}/customers/${customer.id}`, payload);
      else await axios.post(`${API}/customers`, payload);
      toast.success(customer?.id ? "Cliente aggiornato" : "Cliente creato");
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader><DialogTitle>{customer?.id ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Ragione sociale *</Label>
            <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-white border-stone-300" data-testid="cust-business-name" />
          </div>
          <div><Label>Contatto referente</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>P. IVA *</Label>
            <div className="flex gap-1.5">
              <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="bg-white border-stone-300" data-testid="cust-vat" />
              <VatLookupButton vat={form.vat_number} currentSdi={form.sdi_code} onFill={(d) => setForm((f) => ({ ...f, ...d }))} />
            </div>
          </div>
          <div><Label>Codice Fiscale</Label><Input value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Codice SDI</Label><Input value={form.sdi_code} onChange={(e) => setForm({ ...form, sdi_code: e.target.value })} className="bg-white border-stone-300" data-testid="cust-sdi" /></div>
          <div><Label>PEC</Label><Input value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} className="bg-white border-stone-300" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-stone-300" data-testid="cust-email" /></div>
          <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-stone-300" data-testid="cust-phone" /></div>
          <div><Label>IBAN</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="bg-white border-stone-300" /></div>
          <div className="md:col-span-2"><Label>Indirizzo sede legale *</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-white border-stone-300" data-testid="cust-address" /></div>
          <div><Label>Città *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white border-stone-300" data-testid="cust-city" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CAP</Label><Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="bg-white border-stone-300" /></div>
            <div><Label>Prov.</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="bg-white border-stone-300" /></div>
          </div>
          <div className="md:col-span-2 border-t border-stone-200 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-stone-700">🏷️ Listino dedicato (sovrascrive quello dell&apos;agente)</Label>
              <Select value={form.pricelist_type_id || "__none__"} onValueChange={(v) => setForm({ ...form, pricelist_type_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="bg-white border-stone-300" data-testid="cust-pricelist"><SelectValue placeholder="Usa quello dell'agente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Usa quello dell&apos;agente —</SelectItem>
                  {pricelistTypes.map((pt) => <SelectItem key={pt.id} value={pt.id}>{pt.name} ({(pt.commission * 100).toFixed(1)}%)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-7">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_foreign} onChange={(e) => setForm({ ...form, is_foreign: e.target.checked })} data-testid="cust-foreign" className="w-4 h-4 accent-[#8A1C2A]" />
                <span className="text-sm"><strong>🌍 Cliente estero</strong> (IVA 0% su tutti gli articoli)</span>
              </label>
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-700">💶 Provvigione dedicata % (opzionale)</Label>
              <Input type="number" step="0.1" min="0" value={form.commission_override} onChange={(e) => setForm({ ...form, commission_override: e.target.value })} placeholder="Es. 1 = 1%" className="bg-white border-stone-300" data-testid="cust-commission-override" />
              <p className="text-[11px] text-stone-500 mt-1">Se impostata vince su articolo, agente e listino</p>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Note interne</Label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" />
          </div>
          <div className="md:col-span-2 border-t border-stone-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Indirizzi di consegna aggiuntivi</Label>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, delivery_addresses: [...(form.delivery_addresses || []), { label: "", address: "", city: "", cap: "", province: "" }] })} data-testid="add-delivery-addr-btn">
                <Plus className="w-3 h-3 mr-1" /> Aggiungi
              </Button>
            </div>
            {(form.delivery_addresses || []).map((d, idx) => (
              <div key={idx} className="border border-stone-200 rounded p-3 mb-2 bg-stone-50/40">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-stone-700">Indirizzo #{idx + 1}</p>
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, delivery_addresses: form.delivery_addresses.filter((_, i) => i !== idx) })}><X className="w-4 h-4 text-red-500" /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <div className="md:col-span-3"><Label className="text-xs">{t.label}</Label><Input value={d.label} onChange={(e) => { const arr = [...form.delivery_addresses]; arr[idx] = { ...arr[idx], label: e.target.value }; setForm({ ...form, delivery_addresses: arr }); }} className="bg-white border-stone-300 h-9" placeholder="Es. Magazzino" /></div>
                  <div className="md:col-span-5"><Label className="text-xs">{t.address}</Label><Input value={d.address} onChange={(e) => { const arr = [...form.delivery_addresses]; arr[idx] = { ...arr[idx], address: e.target.value }; setForm({ ...form, delivery_addresses: arr }); }} className="bg-white border-stone-300 h-9" /></div>
                  <div className="md:col-span-2"><Label className="text-xs">{t.city}</Label><Input value={d.city} onChange={(e) => { const arr = [...form.delivery_addresses]; arr[idx] = { ...arr[idx], city: e.target.value }; setForm({ ...form, delivery_addresses: arr }); }} className="bg-white border-stone-300 h-9" /></div>
                  <div className="md:col-span-1"><Label className="text-xs">CAP</Label><Input value={d.cap} onChange={(e) => { const arr = [...form.delivery_addresses]; arr[idx] = { ...arr[idx], cap: e.target.value }; setForm({ ...form, delivery_addresses: arr }); }} className="bg-white border-stone-300 h-9" /></div>
                  <div className="md:col-span-1"><Label className="text-xs">Prov.</Label><Input value={d.province} onChange={(e) => { const arr = [...form.delivery_addresses]; arr[idx] = { ...arr[idx], province: e.target.value }; setForm({ ...form, delivery_addresses: arr }); }} className="bg-white border-stone-300 h-9" /></div>
                  <div className="md:col-span-12"><Label className="text-xs">Note (es. orari, citofono, riferimento)</Label><Input value={d.notes || ""} onChange={(e) => { const arr = [...form.delivery_addresses]; arr[idx] = { ...arr[idx], notes: e.target.value }; setForm({ ...form, delivery_addresses: arr }); }} className="bg-white border-stone-300 h-9" placeholder="Note specifiche per questa consegna" /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={save} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="save-customer-btn">Salva cliente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const generatePwd = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const CreateCustomerLoginDialog = ({ customer, onClose }) => {
  const { t } = useLang();
  const [password, setPassword] = useState(generatePwd());
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async () => {
    if (!password || password.length < 4) { toast.error(t.pAgent_passwordTooShort); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/customers/${customer.id}/create-login`, { password });
      setDone({ email: r.data.email, password, updated: r.data.updated });
      toast.success(r.data.updated ? "Password aggiornata" : "Login creato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const copyText = () => {
    const text = `Portale Giotto Carni\nEmail: ${done.email}\nPassword: ${done.password}\nLogin: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text).then(() => toast.success(t.pAgent_credentialsCopied));
  };

  const shareWhatsapp = () => {
    const phone = (customer?.phone || "").replace(/[^0-9+]/g, "");
    const msg = encodeURIComponent(
      `Benvenuto sul portale Giotto Carni!\n\n` +
      `Le tue credenziali di accesso:\n` +
      `Email: ${done.email}\n` +
      `Password: ${done.password}\n\n` +
      `Accedi qui: ${window.location.origin}/login`
    );
    const url = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank");
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white" data-testid="create-login-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#8A1C2A]" /> Credenziali cliente</DialogTitle>
        </DialogHeader>
        {!done ? (
          <div className="space-y-3">
            <div className="bg-stone-50 p-3 rounded text-sm">
              <p><strong>{customer.business_name}</strong></p>
              <p className="text-xs text-stone-600">Email login: {customer.email}</p>
            </div>
            <div>
              <Label>Password iniziale</Label>
              <div className="flex gap-2 mt-1">
                <Input value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white border-stone-300 font-mono" data-testid="cust-login-password" />
                <Button variant="outline" onClick={() => setPassword(generatePwd())} className="border-stone-300">↻</Button>
              </div>
              <p className="text-xs text-stone-500 mt-1">Il cliente potrà cambiarla in seguito. Condividila via WhatsApp.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
              <Button onClick={submit} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="confirm-create-login-btn">
                {saving ? "..." : (customer?.has_login ? "Reimposta password" : "Crea login")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <Card className="bg-green-50 border-green-200 p-3 text-sm">
              <p className="font-semibold text-green-900 mb-2">✓ {done.updated ? "Password aggiornata" : "Login creato con successo"}</p>
              <p><strong>Email:</strong> {done.email}</p>
              <p><strong>Password:</strong> <span className="font-mono">{done.password}</span></p>
            </Card>
            <DialogFooter className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={copyText} className="border-stone-300"><Copy className="w-4 h-4 mr-1" /> Copia</Button>
              <Button onClick={shareWhatsapp} className="bg-green-600 hover:bg-green-700 text-white" data-testid="share-login-whatsapp"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
              <Button onClick={onClose} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CustomersList = () => {
  const { t } = useLang();
  const [customers, setCustomers] = useState([]);
  const capInfo = useCapsSummary(customers.map((c) => c.cap));
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loginCustomer, setLoginCustomer] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [creditMap, setCreditMap] = useState({});
  const [plafondCustomer, setPlafondCustomer] = useState(null);

  const loadCredit = () => axios.get(`${API}/customers-credit-summary`).then((r) => setCreditMap(r.data || {})).catch(() => {});
  useEffect(() => { loadCredit(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const r = await axios.get(`${API}/customers?${params}`);
      setCustomers(r.data || []);
    } catch { toast.error(t.pAgent_loadError); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    const tm = setTimeout(load, 300);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const del = async (id) => {
    if (!window.confirm("Eliminare il cliente?")) return;
    await axios.delete(`${API}/customers/${id}`);
    toast.success(t.pAgent_loginCreated);
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">{t.pAgent_anagraficaClienti}</h1>
          <p className="text-stone-500 text-sm mt-1">{customers.length} {t.customers}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowImport(true)} variant="outline" className="border-[#C5A059] text-[#8A1C2A]" data-testid="agent-bulk-import-btn">
            <Upload className="w-4 h-4 mr-1" /> Importa CSV/XLSX
          </Button>
          <Button onClick={() => setCreating(true)} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="new-customer-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo cliente
          </Button>
        </div>
      </div>

      <div className="mb-4 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input placeholder={t.pAgent_searchCustomer} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="customers-search" />
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
          : customers.length === 0 ? <div className="p-8 text-center text-stone-500"><UsersIcon className="w-12 h-12 mx-auto mb-3 text-stone-300" /><p>{t.pAgent_emptyCustomers}</p></div>
            : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">Codice</th>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">Ragione sociale</th>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">P. IVA</th>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">Plafond</th>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">{t.city}</th>
                        <th className="text-left p-3 text-xs font-semibold text-stone-600">Contatti</th>
                        <th className="text-center p-3 text-xs font-semibold text-stone-600">Login</th>
                        <th className="w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((c) => (
                        <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`customer-row-${c.codice_cliente}`}>
                          <td className="p-3 font-mono text-xs font-semibold text-[#8A1C2A]">{c.codice_cliente}</td>
                          <td className="p-3 font-medium">{c.business_name}{c.contact_name && <div className="text-xs text-stone-500">{c.contact_name}</div>}</td>
                          <td className="p-3 font-mono text-xs">{c.vat_number}</td>
                          <td className="p-3" data-testid={`plafond-cell-${c.id}`}>
                            <PlafondCell info={creditMap[c.id]} testId={`ag-plafond-${c.codice_cliente}`} />
                            {creditMap[c.id] && !creditMap[c.id].insurance_status && !(creditMap[c.id].credit_limit > 0) && !creditMap[c.id].pending_request && (
                              <button className="block text-[11px] text-[#8A1C2A] underline mt-0.5" onClick={() => setPlafondCustomer(c)} data-testid={`ag-plafond-req-${c.codice_cliente}`}>Richiedi plafond</button>
                            )}
                          </td>
                          <td className="p-3 text-xs">{c.city} {c.province && `(${c.province})`}{c.cap && <div className="mt-1"><DeliveryBadges info={capInfo(c.cap)} testid={`ag-cust-delivery-${c.codice_cliente}`} /></div>}</td>
                          <td className="p-3 text-xs"><div>{c.email}</div><div className="text-stone-500">{c.phone}</div></td>
                          <td className="p-3 text-center">
                            {c.has_login ? <Badge className="bg-green-100 text-green-700 text-xs"><Check className="w-3 h-3 mr-1 inline" /> Attivo</Badge> : <Badge className="bg-stone-100 text-stone-500 text-xs">Non creato</Badge>}
                          </td>
                          <td className="p-3 text-right whitespace-nowrap">
                            <Button size="icon" variant="ghost" onClick={() => setLoginCustomer(c)} title={c.has_login ? "Reimposta password" : "Crea login"} data-testid={`login-customer-${c.codice_cliente}`}><KeyRound className="w-4 h-4 text-[#C5A059]" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => setEditing(c)} data-testid={`edit-customer-${c.codice_cliente}`}><Edit className="w-4 h-4 text-stone-500" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-stone-100">
                  {customers.map((c) => (
                    <div key={c.id} className="p-4" data-testid={`customer-card-${c.codice_cliente}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs text-[#8A1C2A]">{c.codice_cliente}</p>
                          <p className="font-medium truncate">{c.business_name}</p>
                          {c.contact_name && <p className="text-xs text-stone-500">{c.contact_name}</p>}
                        </div>
                        {c.has_login ? <Badge className="bg-green-100 text-green-700 text-xs flex-shrink-0">Login OK</Badge> : <Badge className="bg-stone-100 text-stone-500 text-xs flex-shrink-0">No login</Badge>}
                      </div>
                      <p className="text-xs text-stone-600">P.IVA: {c.vat_number}</p>
                      <div className="mt-1"><PlafondCell info={creditMap[c.id]} /></div>
                      <p className="text-xs text-stone-600">{c.city} {c.province && `(${c.province})`}</p>
                      {c.cap && <div className="mt-1"><DeliveryBadges info={capInfo(c.cap)} /></div>}
                      <p className="text-xs text-stone-500 truncate">{c.email} · {c.phone}</p>
                      <div className="flex items-center gap-1 mt-2 -ml-1">
                        <Button size="sm" variant="ghost" onClick={() => setLoginCustomer(c)} className="text-xs"><KeyRound className="w-3 h-3 mr-1 text-[#C5A059]" /> Login</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(c)} className="text-xs"><Edit className="w-3 h-3 mr-1" /> Modifica</Button>
                        <Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-xs text-red-600"><Trash2 className="w-3 h-3 mr-1" /> Elimina</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
      </Card>

      {(editing || creating) && <CustomerForm customer={editing} onClose={() => { setEditing(null); setCreating(false); }} onSave={() => { setEditing(null); setCreating(false); load(); }} />}
      {loginCustomer && <CreateCustomerLoginDialog customer={loginCustomer} onClose={() => { setLoginCustomer(null); load(); }} />}
      {showImport && <BulkImportCustomersDialog agents={[]} agentMode onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      <PlafondRequestDialog customer={plafondCustomer} open={!!plafondCustomer} onClose={() => setPlafondCustomer(null)} onDone={loadCredit} />
    </div>
  );
};

export default CustomersList;
